import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Auth } from '../contexts/AuthContext';
import { getStoredRoles } from '../shared/authSession';
import { isOnCorrectSubdomain, redirectToOrganizationSubdomain } from '../shared/subdomainRedirect';

const PrivateRoute = ({ element }) => {
    const { state, loading } = Auth();
    const location = useLocation();

    if (loading || !state.isAuthenticated || !state.token) {
        // Preserve the intended destination (including ?post= deep-link params)
        // so the login page can redirect back after successful authentication.
        return <Navigate to="/sign-in" state={{ from: location }} replace />;
    }

    if (state.requiresOrganizationSetup && location.pathname !== "/organization-setup") {
        return <Navigate to="/organization-setup" replace />;
    }

    // Every protected route renders through here, so this one check covers direct
    // navigation, bookmarks, and deep links landing on the wrong host — bounces the
    // browser (real cross-origin navigation, React Router can't do this) to the
    // organization's own subdomain. No-op once already on the right host, or when
    // the org has no subdomain configured yet.
    if (state.organizationSubdomain && !isOnCorrectSubdomain(state.organizationSubdomain)) {
        redirectToOrganizationSubdomain(state.organizationSubdomain, location.pathname + location.search);
        return null;
    }

    return element;
};

export const AdminRoute = ({ element }) => {
    const { state, loading } = Auth();
    const location = useLocation();

    if (loading || !state.isAuthenticated || !state.token) {
        return <Navigate to="/sign-in" state={{ from: location }} replace />;
    }

    if (state.requiresOrganizationSetup && location.pathname !== "/organization-setup") {
        return <Navigate to="/organization-setup" replace />;
    }

    if (state.organizationSubdomain && !isOnCorrectSubdomain(state.organizationSubdomain)) {
        redirectToOrganizationSubdomain(state.organizationSubdomain, location.pathname + location.search);
        return null;
    }

    const roles = Array.isArray(state?.role) && state.role.length > 0 ? state.role : getStoredRoles();
    if (!roles.includes("ROLE_ADMIN")) {
        return <Navigate to="/employees/timeline" replace />;
    }

    return element;
};

export default PrivateRoute;
